import {
  iceHints,
  IceBoard,
  NO_HINTS,
  RUNESTONE_NAMES,
  SIDE_COLORS,
  SIDE_NAMES,
  SINK_SENTENCES,
  WARRIOR_NAMES,
  type IceCameraView,
  type Square,
} from '@halvard/board3d';
import { ice, squareName } from '@halvard/rules';
import { ICE_BOT_LEVELS } from '@halvard/ai';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useStore } from 'zustand';
import {
  setMotionPreference,
  useMotionSettings,
  type MotionPreference,
} from '../../hooks/useReducedMotion';
import '../LocalGame.css';
import './IceLocalGame.css';
import { outcomeText, shoveSums, warriorName } from './describe';
import { isBotTurn, sideToMoveText, type IceGameStoreApi, type IcePlayers } from './store';

const VIEWS: { id: IceCameraView; label: string }[] = [
  { id: 'brand', label: 'Brand side' },
  { id: 'rime', label: 'Rime side' },
  { id: 'top', label: 'Top' },
];

const MOTION: { id: MotionPreference; label: string }[] = [
  { id: 'auto', label: 'Auto' },
  { id: 'on', label: 'On' },
  { id: 'off', label: 'Off' },
];

const RULE: Readonly<Record<ice.WarriorType, string>> = {
  skier: 'Force 1, 4 move points. Fast, but easy to shove.',
  spear: 'Force 2, 3 move points.',
  huscarl: 'Force 3, 2 move points. Slow and heavy.',
};

function Dot({ side }: { side: ice.Side }) {
  return (
    <span
      className="ice-dot"
      aria-hidden="true"
      style={{ background: SIDE_COLORS[side].body, borderColor: SIDE_COLORS[side].trim }}
    />
  );
}

/** The last action's squares: where the warrior started and where it acted. */
function lastSquares(game: ice.IceState, before: ice.IceState | undefined): Square[] {
  if (!before) return [];
  const moved = game.warriors.filter((w) => {
    const old = before.warriors.find((b) => b.id === w.id);
    return !old || old.r !== w.r || old.c !== w.c;
  });
  const squares = moved.flatMap((w) => {
    const old = before.warriors.find((b) => b.id === w.id);
    return old ? [old, w] : [w];
  });
  // A shover lands where its target stood: keep each square once.
  return squares
    .filter((sq, i) => squares.findIndex((o) => o.r === sq.r && o.c === sq.c) === i)
    .map(({ r, c }) => ({ r, c }));
}

export interface IceLocalGameProps {
  store: IceGameStoreApi;
  /** Local games: start over. Online games leave them out and use `resultActions`. */
  onNewLake?: () => void;
  onReplay?: () => void;
  /** Buttons under a finished game's result, in place of Replay / New lake. */
  resultActions?: ReactNode;
  /** Drawn over the board, e.g. the screen between the two games of a match. */
  overlay?: ReactNode;
  /** Replaces the turn line, e.g. "Waiting for your friend". */
  status?: string;
  /** Extra controls at the top of the panel (bot games). */
  panelExtra?: ReactNode;
}

/** "Hard bot", "You", the opponent's name, or "Rime player" when two people share the screen. */
export function playerLabel(
  players: IcePlayers,
  side: ice.Side,
  names: Partial<Record<ice.Side, string>> = {},
): string {
  const player = players[side];
  if (player === 'remote') return names[side] ?? 'Opponent';
  if (player !== 'human') {
    return `${ICE_BOT_LEVELS.find((l) => l.id === player)?.name ?? player} bot`;
  }
  const other = players[side === 'brand' ? 'rime' : 'brand'];
  return other === 'human' ? `${SIDE_NAMES[side]} player` : 'You';
}

export function IceLocalGame(props: IceLocalGameProps) {
  const { store, onNewLake, onReplay, panelExtra, resultActions, overlay } = props;
  const s = useStore(store);
  const game = s.game;
  const motion = useMotionSettings();
  const reducedMotion = motion.reduced;
  const [sheetOpen, setSheetOpen] = useState(false);
  const log = useRef<HTMLOListElement>(null);
  const over = game.phase === 'over';

  const botTurn = isBotTurn(s);
  // No hints while a bot or an online opponent is to move (or nobody can move yet), nor once
  // the game is over.
  const hints = useMemo(
    () => (botTurn ? NO_HINTS : iceHints(game, s.selectedId, s.target, s.focus)),
    [botTurn, game, s.selectedId, s.target, s.focus],
  );
  const selected = game.warriors.find((w) => w.id === s.selectedId) ?? null;
  const lastMove = useMemo(() => lastSquares(game, s.history.at(-1)?.before), [game, s.history]);
  const mustPass = useMemo(() => {
    const legal = ice.legalIceActions(game);
    return legal.length === 1 && legal[0]?.kind === 'pass';
  }, [game]);
  const holders = ice.runestoneHolders(game);
  const { roundLimit, gloryTarget } = game.config;
  const brandTurnsLeft = roundLimit - game.round + (game.turn === 'brand' ? 1 : 0);
  const result =
    over && game.winner && game.winReason ? outcomeText(game.winner, game.winReason) : null;
  const anyBot = s.players.brand !== 'human' || s.players.rime !== 'human';
  const turnLine = botTurn
    ? `${SIDE_NAMES[game.turn]} (${playerLabel(s.players, game.turn, s.names)}) ${s.thinking ? 'is thinking…' : game.phase === 'placing' ? 'places a warrior' : 'to move'}`
    : sideToMoveText(game);
  const status = result ? result.title : (props.status ?? turnLine);
  const left = ice.toPlace(game, game.turn);

  useEffect(() => {
    const list = log.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [s.history]);

  const choice = hints.choice;
  const choiceShover = choice && game.warriors.find((w) => w.id === choice.action.id);
  const choiceTarget =
    choice && ice.warriorAt(game, choice.action.target.r, choice.action.target.c);

  return (
    <div className="game-layout">
      <section className="game-boardArea" aria-label="Lake">
        <IceBoard
          state={game}
          hints={hints}
          selected={selected ? { r: selected.r, c: selected.c } : null}
          cursor={s.cursor}
          lastMove={lastMove}
          playback={s.playback}
          view={s.view}
          facing={s.view === 'rime' ? 'rime' : 'brand'}
          reducedMotion={reducedMotion}
          label={`Lake #${game.lake.seed}. ${status}. Use the arrow keys to move, Enter to select.`}
          onSquareActivate={s.activate}
          onSquareFocus={s.setFocus}
          onCursorChange={s.setCursor}
          onCancel={s.cancel}
        />
        {result && (
          <div className="game-banner" role="alert">
            <p className="game-bannerTitle">{result.title}</p>
            <p className="game-bannerDetail">
              {result.detail} Glory: {game.glory} of {gloryTarget}.
            </p>
            <div className="game-row">
              {resultActions ?? (
                <>
                  <button type="button" className="btn btn-primary" onClick={onReplay}>
                    Replay this lake
                  </button>
                  <button type="button" className="btn" onClick={onNewLake}>
                    New lake
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        {overlay}
      </section>

      <aside className={`game-panel ${sheetOpen ? 'game-open' : ''}`} aria-label="Game controls">
        <button
          type="button"
          className="game-sheetHandle"
          aria-expanded={sheetOpen}
          aria-controls="ice-panel-body"
          onClick={() => setSheetOpen((open) => !open)}
        >
          <span className="game-turn">
            {!result && <Dot side={game.turn} />}
            {status}
          </span>
          <span className="label">{sheetOpen ? 'Hide' : 'Controls'}</span>
        </button>

        <div id="ice-panel-body" className="game-panelBody">
          <div className="game-section">
            <p className="game-turnDesktop">
              {!result && <Dot side={game.turn} />}
              <span>{status}</span>
              {game.phase !== 'placing' && (
                <span className="label">
                  Round {game.round} of {roundLimit}
                </span>
              )}
            </p>
            <p className="label">Lake #{game.lake.seed}</p>
            {anyBot && (
              <ul className="ice-stones" aria-label="Players">
                {(['brand', 'rime'] as const).map((side) => (
                  <li key={side}>
                    <Dot side={side} />
                    <span>{SIDE_NAMES[side]}</span>
                    <span className="game-hint">{playerLabel(s.players, side, s.names)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {panelExtra}

          <div className="game-section">
            <h2 className="label">Glory</h2>
            <div
              className="ice-meter"
              role="meter"
              aria-label="Brand's Glory"
              aria-valuemin={0}
              aria-valuemax={gloryTarget}
              aria-valuenow={game.glory}
            >
              <span style={{ width: `${Math.min(100, (game.glory / gloryTarget) * 100)}%` }} />
            </div>
            <p className="game-hint">
              Brand {game.glory} of {gloryTarget}
              {!over && game.phase === 'playing'
                ? `: needs ${Math.max(0, gloryTarget - game.glory)} more in ${brandTurnsLeft} turn${brandTurnsLeft === 1 ? '' : 's'}`
                : ''}
              .
            </p>
            <ul className="ice-stones" aria-label="Runestones">
              {RUNESTONE_NAMES.map((stoneName, i) => {
                const holder = holders[i] ?? null;
                return (
                  <li key={stoneName}>
                    {holder ? (
                      <Dot side={holder} />
                    ) : (
                      <span className="ice-dot ice-dotEmpty" aria-hidden="true" />
                    )}
                    <span>{stoneName[0]?.toUpperCase() + stoneName.slice(1)}</span>
                    <span className="game-hint">
                      {holder ? `${SIDE_NAMES[holder]} holds` : 'nobody'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          {game.phase === 'placing' && !botTurn && (
            <div className="game-section">
              <h2 className="label">Place a warrior</h2>
              <div className="segmented" role="group" aria-label="Warrior to place">
                {ice.WARRIOR_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={s.placeType === type}
                    disabled={left[type] === 0}
                    onClick={() => s.setPlaceType(type)}
                  >
                    {WARRIOR_NAMES[type]} ×{left[type]}
                  </button>
                ))}
              </div>
              <p className="game-rule">{RULE[s.placeType]}</p>
              <p className="game-hint">
                Click a free square in {SIDE_NAMES[game.turn]}’s territory (ranks{' '}
                {game.turn === 'brand' ? '1–3' : '7–9'}).
              </p>
            </div>
          )}

          {game.phase === 'playing' && !botTurn && (
            <div className="game-section">
              {selected ? (
                <>
                  <p className="game-pieceTitle">
                    <Dot side={selected.side} />
                    <span>
                      {warriorName(selected)}{' '}
                      <span className="mono">{squareName(selected.r, selected.c)}</span>
                    </span>
                  </p>
                  <p className="game-rule">{RULE[selected.type]}</p>
                  {choice && choiceShover && choiceTarget ? (
                    <div className="ice-shove" aria-live="polite">
                      <p>
                        <strong>Shove {squareName(choiceTarget.r, choiceTarget.c)}</strong> from{' '}
                        {squareName(choice.action.from.r, choice.action.from.c)}:{' '}
                        {shoveSums(choiceShover, choiceTarget, choice.strength, choice.wall)}
                      </p>
                      {hints.ghosts
                        .filter((g) => g.sunk)
                        .map((g) => (
                          <p
                            key={g.warrior.id}
                            className={g.warrior.side === selected.side ? 'ice-warn' : ''}
                          >
                            {warriorName(g.warrior)} {SINK_SENTENCES[g.sunk ?? 'off']}.
                          </p>
                        ))}
                      <p className="game-hint">
                        {s.target
                          ? 'Point at another diamond to shove from there; click to play.'
                          : 'Click the enemy to pick it, then choose where to shove from.'}
                      </p>
                    </div>
                  ) : (
                    <p className="game-hint">
                      {s.target
                        ? 'That shove fails: the shield wall holds. Try another square.'
                        : 'Dots: where it can walk. Numbers over an enemy: your shove against its shield wall.'}
                    </p>
                  )}
                </>
              ) : (
                <p className="game-hint">Select one of your warriors.</p>
              )}
            </div>
          )}

          <div className="game-section game-row">
            {mustPass && !over && !botTurn && (
              <button type="button" className="btn btn-small btn-primary" onClick={s.pass}>
                Pass (no move)
              </button>
            )}
            <button
              type="button"
              className="btn btn-small"
              onClick={s.undo}
              disabled={
                s.history.length === 0 ||
                (s.players.brand !== 'human' && s.players.rime !== 'human') ||
                s.players.brand === 'remote' ||
                s.players.rime === 'remote'
              }
            >
              Undo
            </button>
          </div>

          <div className="game-section">
            <h2 className="label">Cracks</h2>
            {game.cracks.length === 0 ? (
              <p className="game-hint">
                {game.phase === 'playing'
                  ? `The ice groans at the end of round ${Math.ceil(game.round / game.config.crackEvery) * game.config.crackEvery}.`
                  : 'None yet.'}
              </p>
            ) : (
              <ul className="ice-cracks">
                {game.cracks.map((crack) => (
                  <li key={`${crack.r}:${crack.c}`}>
                    <span className="mono">{squareName(crack.r, crack.c)}</span>{' '}
                    {game.heights[crack.r]?.[crack.c] === 0 ? (
                      <span className="ice-warn">opens a hole</span>
                    ) : (
                      'thins'
                    )}{' '}
                    at the end of round {crack.thawRound}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="game-section">
            <h2 className="label">Log</h2>
            {s.history.length === 0 ? (
              <p className="game-hint">Brand places first, then the sides take turns.</p>
            ) : (
              <ol className="ice-log" ref={log} aria-label="Move log">
                {s.history.map((turn, i) => (
                  <li key={i}>
                    <Dot side={turn.side} />
                    <span className="mono">{turn.notation}</span>
                    <span className="game-hint">{turn.lines.join(' ')}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="game-section">
            <h2 className="label">View</h2>
            <div className="segmented" role="group" aria-label="Camera view">
              {VIEWS.map((view) => (
                <button
                  key={view.id}
                  type="button"
                  aria-pressed={s.view === view.id}
                  onClick={() => s.setView(view.id)}
                >
                  {view.label}
                </button>
              ))}
            </div>
            <p className="label ice-subhead">Animations</p>
            <div className="segmented" role="group" aria-label="Animations">
              {MOTION.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={motion.preference === option.id}
                  onClick={() => setMotionPreference(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {motion.preference === 'auto' && motion.systemReduces && (
              <p className="game-hint">
                Your system asks apps to reduce motion, so warriors move without animating. Choose
                On to see them walk.
              </p>
            )}
          </div>

          {onNewLake && onReplay && (
            <div className="game-section game-row">
              <button type="button" className="btn btn-small" onClick={onNewLake}>
                New lake
              </button>
              <button type="button" className="btn btn-small" onClick={onReplay}>
                Replay this lake
              </button>
            </div>
          )}
        </div>
      </aside>

      <p className="visually-hidden" role="status" aria-live="polite">
        {s.announcement}
      </p>
    </div>
  );
}
